import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaArrowLeft, FaPlus, FaTrash } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

import RestaurantDashboardLayout from './RestaurantDashboardLayout';
import { useRestaurantEnabledServices } from './useRestaurantEnabledServices';
import {
    CATERING_QUOTES_LIST_PATH,
    createCateringQuote,
} from './cateringQuoteApi';
import { fetchCateringMenuItemsForTemplate } from './cateringMenuApi';
import { fetchCateringTempById, fetchCateringTemps } from './cateringTempApi';
import { getLocalDateString } from './dailyMenuApi';
import { getActiveRestaurantId } from './entityContext';
import { extractRestaurantId, extractRestaurantName, resolveInitialRestaurant } from './restaurantApi';
import AddressSuggestInput from './AddressSuggestInput';
import { formatMobileDisplay, sanitizeMobileInput, validateMobileNumber } from './formatDisplay';
import './css/Menu.css';
import './css/Restaurants.css';
import './css/OrderDetails.css';
import './css/cateringQuoteCreate.css';
import './css/cateringViewSwitch.css';

const MENU_MODE_STANDARD = 'standard';
const MENU_MODE_CUSTOMIZATION = 'customization';

function getActorId() {
    try {
        const user = JSON.parse(localStorage.getItem('user') || 'null');
        return Number(user?.userId ?? user?.User_Id ?? user?.user_id ?? 0);
    } catch (e) {
        return 0;
    }
}

function getLoggedInRestaurantId() {
    const stored = getActiveRestaurantId();
    if (stored && stored !== 'null' && stored !== 'undefined') {
        const id = Number(stored);
        if (Number.isFinite(id) && id > 0) return id;
    }
    try {
        const user = JSON.parse(localStorage.getItem('user') || 'null');
        const fromUser = Number(
            user?.restaurantId ?? user?.Restaurant_Id ?? user?.restaurant_id ?? 0
        );
        if (Number.isFinite(fromUser) && fromUser > 0) return fromUser;
    } catch (e) {
        // ignore
    }
    return null;
}

function emptyLineRow() {
    return {
        localKey: `${Date.now()}-${Math.random()}`,
        cateringItemId: '',
        quantity: '1',
    };
}

function formatMoney(value) {
    const n = Number.parseFloat(value);
    if (!Number.isFinite(n)) return '0.00';
    return n.toFixed(2);
}

const CreateCateringQuote = () => {
    const navigate = useNavigate();
    const { enabledTypes: enabledServiceTypes, loading: servicesLoading } =
        useRestaurantEnabledServices();
    const [restaurantId, setRestaurantId] = useState(() => getLoggedInRestaurantId());
    const [resolvingRestaurant, setResolvingRestaurant] = useState(() => !getLoggedInRestaurantId());
    const actorId = getActorId();

    useEffect(() => {
        let cancelled = false;

        const applyRestaurant = (id) => {
            if (cancelled || !id) return;
            setRestaurantId(Number(id));
            setResolvingRestaurant(false);
        };

        const immediate = getLoggedInRestaurantId();
        if (immediate) {
            applyRestaurant(immediate);
            return undefined;
        }

        const resolve = async () => {
            try {
                const user = JSON.parse(localStorage.getItem('user') || 'null');
                const userId = user?.userId ?? user?.User_Id ?? user?.user_id;
                if (!userId) {
                    if (!cancelled) setResolvingRestaurant(false);
                    return;
                }
                const initial = await resolveInitialRestaurant(userId);
                const id = extractRestaurantId(initial);
                const name = extractRestaurantName(initial);
                if (id) {
                    localStorage.setItem('restaurantId', String(id));
                    if (name) localStorage.setItem('restaurantName', name);
                    applyRestaurant(id);
                    return;
                }
            } catch (e) {
                // ignore
            }
            if (!cancelled) setResolvingRestaurant(false);
        };

        resolve();
        return () => {
            cancelled = true;
        };
    }, []);

    const [menuItems, setMenuItems] = useState([]);
    const [packages, setPackages] = useState([]);
    const [loadingMenu, setLoadingMenu] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const [customerName, setCustomerName] = useState('');
    const [customerContact, setCustomerContact] = useState('');
    const [eventDate, setEventDate] = useState('');
    const [eventTime, setEventTime] = useState('');
    const [eventType, setEventType] = useState('');
    const [numberOfGuests, setNumberOfGuests] = useState('');
    const [deliveryType, setDeliveryType] = useState('DELIVERY');
    const [deliveryAddress, setDeliveryAddress] = useState('');
    const [cateringTempId, setCateringTempId] = useState('');
    const [packageName, setPackageName] = useState('');
    const [menuMode, setMenuMode] = useState(MENU_MODE_STANDARD);
    const [packageItems, setPackageItems] = useState([]);
    const [loadingPackage, setLoadingPackage] = useState(false);
    const [estimatedQuote, setEstimatedQuote] = useState('');
    const [lineRows, setLineRows] = useState([emptyLineRow()]);

    const minEventDate = useMemo(() => getLocalDateString(), []);
    const loadReferenceData = useCallback(async () => {
        if (!restaurantId) {
            setLoadingMenu(false);
            return;
        }
        setLoadingMenu(true);
        try {
            const [items, pkgList] = await Promise.all([
                fetchCateringMenuItemsForTemplate(restaurantId),
                fetchCateringTemps(restaurantId),
            ]);
            setMenuItems(items ?? []);
            setPackages((pkgList ?? []).filter((pkg) => pkg.status !== false));
        } catch (err) {
            setError(err?.response?.data?.error || err.message || 'Failed to load catering menu');
        } finally {
            setLoadingMenu(false);
        }
    }, [restaurantId]);

    useEffect(() => {
        loadReferenceData();
    }, [loadReferenceData]);

    const packageItemRows = useMemo(
        () =>
            (packageItems ?? []).map((pkgItem) => ({
                Catering_Item_Id: pkgItem.cateringItemId,
                Food_Item: pkgItem.itemName || `Item #${pkgItem.cateringItemId}`,
                foodCategoryId: pkgItem.foodCategoryId,
                Price: pkgItem.price,
            })),
        [packageItems]
    );

    const selectableMenuItems = useMemo(
        () => (menuMode === MENU_MODE_STANDARD ? packageItemRows : menuItems),
        [menuMode, packageItemRows, menuItems]
    );

    const selectedPackage = useMemo(() => {
        if (!cateringTempId) return null;
        return packages.find((p) => String(p.cateringTempId) === String(cateringTempId)) || null;
    }, [packages, cateringTempId]);

    const packagePricePerPerson = useMemo(() => {
        const raw = selectedPackage?.pricePerPerson;
        if (raw == null || raw === '') return null;
        const n = Number.parseFloat(raw);
        return Number.isFinite(n) && n >= 0 ? n : null;
    }, [selectedPackage]);

    useEffect(() => {
        if (menuMode !== MENU_MODE_STANDARD) return;
        if (packagePricePerPerson == null) {
            if (!cateringTempId) setEstimatedQuote('');
            return;
        }
        const guests = Number.parseInt(numberOfGuests, 10);
        if (!Number.isFinite(guests) || guests <= 0) {
            setEstimatedQuote('');
            return;
        }
        const total = Math.round(packagePricePerPerson * guests * 100) / 100;
        setEstimatedQuote(total.toFixed(2));
    }, [menuMode, packagePricePerPerson, numberOfGuests, cateringTempId]);

    const itemLookupById = useMemo(() => {
        const map = new Map();
        selectableMenuItems.forEach((item) => {
            map.set(String(item.Catering_Item_Id), item);
        });
        return map;
    }, [selectableMenuItems]);

    const resetMenuSelection = () => {
        setCateringTempId('');
        setPackageName('');
        setPackageItems([]);
        setLineRows([emptyLineRow()]);
    };

    const handleMenuModeChange = (mode) => {
        if (mode === menuMode) return;
        setMenuMode(mode);
        resetMenuSelection();
        setError('');
    };

    const updateLineRow = (localKey, patch) => {
        setLineRows((rows) =>
            rows.map((row) => (row.localKey === localKey ? { ...row, ...patch } : row))
        );
    };

    const removeLineRow = (localKey) => {
        setLineRows((rows) => {
            const next = rows.filter((row) => row.localKey !== localKey);
            return next.length > 0 ? next : [emptyLineRow()];
        });
    };

    const handlePackageChange = async (value) => {
        setCateringTempId(value);
        setPackageItems([]);
        setLineRows([emptyLineRow()]);
        if (!value) {
            setPackageName('');
            return;
        }
        const pkg = packages.find((p) => String(p.cateringTempId) === String(value));
        setPackageName(pkg?.packageName || '');
        setLoadingPackage(true);
        setError('');
        try {
            const data = await fetchCateringTempById(Number(value));
            const items = (Array.isArray(data?.items) ? data.items : []).filter(
                (row) => row?.cateringItemId != null
            );
            setPackageItems(items);
            const rows = items.map((pkgItem) => ({
                localKey: `${pkgItem.cateringItemId}-${Date.now()}-${Math.random()}`,
                cateringItemId: String(pkgItem.cateringItemId),
                quantity: '1',
            }));
            setLineRows(rows.length > 0 ? rows : [emptyLineRow()]);
        } catch (err) {
            setError(
                err?.response?.data?.error ||
                    err?.response?.data?.message ||
                    err.message ||
                    'Failed to load package items'
            );
        } finally {
            setLoadingPackage(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (saving) return;
        setError('');

        if (!restaurantId) {
            setError('Logged-in restaurant is required. Sign in again or select a restaurant from the header.');
            return;
        }
        if (!actorId) {
            setError('Signed-in user is required.');
            return;
        }
        if (!customerName.trim()) {
            setError('Customer name is required.');
            return;
        }
        const phoneDigits = sanitizeMobileInput(customerContact);
        const phoneError = validateMobileNumber(phoneDigits, { label: 'Phone number' });
        if (phoneError) {
            setError(phoneError);
            return;
        }
        if (!eventDate) {
            setError('Event date is required.');
            return;
        }
        if (eventDate < minEventDate) {
            setError('Event date cannot be in the past.');
            return;
        }
        if (menuMode === MENU_MODE_STANDARD && !cateringTempId) {
            setError('Select a package for standard menu.');
            return;
        }

        const lineItems = [];
        for (const row of lineRows) {
            if (!row.cateringItemId) continue;
            const qty = Number.parseInt(row.quantity, 10);
            if (!Number.isFinite(qty) || qty <= 0) {
                setError('Each line item needs a quantity greater than 0.');
                return;
            }
            const item = itemLookupById.get(String(row.cateringItemId));
            if (!item) {
                setError('Invalid catering menu item selected.');
                return;
            }
            const unitPrice = Number.parseFloat(item.Price);
            lineItems.push({
                cateringItemId: Number(row.cateringItemId),
                itemName: item.Food_Item,
                foodCategoryId: item.foodCategoryId,
                quantity: qty,
                unitPrice: Number.isFinite(unitPrice) ? unitPrice : 0,
                price: Number.isFinite(unitPrice) ? unitPrice * qty : 0,
            });
        }

        if (lineItems.length === 0) {
            setError('Add at least one catering menu item.');
            return;
        }

        const quoteTotal = Number.parseFloat(estimatedQuote);
        if (!Number.isFinite(quoteTotal) || quoteTotal <= 0) {
            setError('Enter a valid estimated quote amount.');
            return;
        }

        const payload = {
            restaurantId,
            createdBy: actorId,
            customerName: customerName.trim(),
            customerContact: phoneDigits,
            eventDate,
            eventTime: eventTime || undefined,
            eventType: eventType.trim() || undefined,
            numberOfGuests: numberOfGuests ? Number.parseInt(numberOfGuests, 10) : undefined,
            deliveryType: deliveryType || 'DELIVERY',
            deliveryAddress: deliveryAddress.trim() || undefined,
            cateringTempId:
                menuMode === MENU_MODE_STANDARD && cateringTempId
                    ? Number.parseInt(cateringTempId, 10)
                    : undefined,
            packageName:
                menuMode === MENU_MODE_STANDARD ? packageName.trim() || undefined : undefined,
            pricePerPerson:
                menuMode === MENU_MODE_STANDARD && packagePricePerPerson != null
                    ? packagePricePerPerson
                    : undefined,
            estimatedTotal: quoteTotal,
            lineItems,
        };

        setSaving(true);
        try {
            const result = await createCateringQuote(payload);
            const quoteId = result?.cateringQuoteId ?? result?.quote?.cateringQuoteId;
            if (quoteId) {
                navigate(`/catering-quote/${quoteId}`);
                return;
            }
            navigate(CATERING_QUOTES_LIST_PATH);
        } catch (err) {
            setError(
                err?.response?.data?.error ||
                    err?.response?.data?.message ||
                    err.message ||
                    'Failed to save quote'
            );
        } finally {
            setSaving(false);
        }
    };

    const handleSelectOrderChannel = (key) => {
        navigate(`/home?tab=orders&orderType=${key}`);
    };

    const showMenuItemsTable =
        menuMode === MENU_MODE_CUSTOMIZATION
            ? !loadingMenu && menuItems.length > 0
            : Boolean(cateringTempId) && !loadingPackage && packageItems.length > 0;

    const canSaveQuote =
        menuMode === MENU_MODE_CUSTOMIZATION
            ? !loadingMenu && menuItems.length > 0
            : !loadingMenu && packages.length > 0;

    return (
        <RestaurantDashboardLayout
            activeTab="orders"
            orderChannel="catering"
            onSelectOrderChannel={handleSelectOrderChannel}
            enabledServiceTypes={enabledServiceTypes}
            servicesLoading={servicesLoading}
        >
            <div className="purchase-detail-page catering-quote-create-page">
                <div className="restaurant-detail-panel">
                    <div className="restaurant-detail-card">
                        <div className="restaurant-detail-card-header inventory-item-detail-card-header">
                            <div className="inventory-item-detail-header-row">
                                <button
                                    type="button"
                                    className="order-details-back-button restaurant-detail-back-button order-details-back-btn"
                                    onClick={() => navigate(CATERING_QUOTES_LIST_PATH)}
                                    aria-label="Back to quote requests"
                                >
                                    <FaArrowLeft aria-hidden />
                                </button>
                                <h3 className="restaurant-detail-section-title">Create Catering Quote</h3>
                            </div>
                        </div>

                        {resolvingRestaurant ? (
                            <p className="catering-quote-create-muted">Loading restaurant…</p>
                        ) : !restaurantId ? (
                            <p className="restaurant-detail-message error">
                                Logged-in restaurant not found. Select a restaurant from the header or sign in again.
                            </p>
                        ) : (
                            <form className="catering-quote-create-form" onSubmit={handleSubmit}>
                                {error ? <p className="restaurant-detail-message error">{error}</p> : null}

                                <section className="catering-quote-create-section">
                                    <h4 className="catering-quote-create-section-title">Customer</h4>
                                    <div className="catering-quote-create-grid">
                                        <label className="catering-quote-create-field">
                                            <span>Name *</span>
                                            <input
                                                type="text"
                                                value={customerName}
                                                onChange={(e) => setCustomerName(e.target.value)}
                                                placeholder="Customer name"
                                                maxLength={200}
                                            />
                                        </label>
                                        <label className="catering-quote-create-field">
                                            <span>Phone *</span>
                                            <input
                                                type="tel"
                                                value={formatMobileDisplay(customerContact)}
                                                onChange={(e) =>
                                                    setCustomerContact(sanitizeMobileInput(e.target.value))
                                                }
                                                placeholder="123-456-7890"
                                                inputMode="numeric"
                                            />
                                        </label>
                                    </div>
                                </section>

                                <section className="catering-quote-create-section">
                                    <h4 className="catering-quote-create-section-title">Event</h4>
                                    <div className="catering-quote-create-grid">
                                        <label className="catering-quote-create-field">
                                            <span>Event date *</span>
                                            <input
                                                type="date"
                                                min={minEventDate}
                                                value={eventDate}
                                                onChange={(e) => setEventDate(e.target.value)}
                                            />
                                        </label>
                                        <label className="catering-quote-create-field">
                                            <span>Event time</span>
                                            <input
                                                type="time"
                                                value={eventTime}
                                                onChange={(e) => setEventTime(e.target.value)}
                                            />
                                        </label>
                                        <label className="catering-quote-create-field">
                                            <span>Event type</span>
                                            <input
                                                type="text"
                                                value={eventType}
                                                onChange={(e) => setEventType(e.target.value)}
                                                placeholder="Wedding, corporate, etc."
                                            />
                                        </label>
                                        <label className="catering-quote-create-field">
                                            <span>Guests</span>
                                            <input
                                                type="number"
                                                min="1"
                                                value={numberOfGuests}
                                                onChange={(e) =>
                                                    setNumberOfGuests(e.target.value.replace(/\D/g, ''))
                                                }
                                            />
                                        </label>
                                        <label className="catering-quote-create-field">
                                            <span>Delivery type</span>
                                            <select
                                                value={deliveryType}
                                                onChange={(e) => setDeliveryType(e.target.value)}
                                            >
                                                <option value="DELIVERY">Delivery</option>
                                                <option value="PICKUP">Pickup</option>
                                            </select>
                                        </label>
                                        <label className="catering-quote-create-field catering-quote-create-field--wide">
                                            <span>Event location</span>
                                            <AddressSuggestInput
                                                value={deliveryAddress}
                                                onChange={setDeliveryAddress}
                                                placeholder="Search venue or delivery address"
                                                aria-label="Event location"
                                            />
                                        </label>
                                    </div>
                                </section>

                                <section className="catering-quote-create-section">
                                    <h4 className="catering-quote-create-section-title">Menu *</h4>
                                    <div
                                        className="catering-view-switch catering-quote-menu-mode-switch"
                                        role="tablist"
                                        aria-label="Menu type"
                                    >
                                        <button
                                            type="button"
                                            role="tab"
                                            aria-selected={menuMode === MENU_MODE_STANDARD}
                                            className={`catering-view-switch__segment${
                                                menuMode === MENU_MODE_STANDARD
                                                    ? ' catering-view-switch__segment--active'
                                                    : ''
                                            }`}
                                            onClick={() => handleMenuModeChange(MENU_MODE_STANDARD)}
                                        >
                                            Standard menu
                                        </button>
                                        <button
                                            type="button"
                                            role="tab"
                                            aria-selected={menuMode === MENU_MODE_CUSTOMIZATION}
                                            className={`catering-view-switch__segment${
                                                menuMode === MENU_MODE_CUSTOMIZATION
                                                    ? ' catering-view-switch__segment--active'
                                                    : ''
                                            }`}
                                            onClick={() => handleMenuModeChange(MENU_MODE_CUSTOMIZATION)}
                                        >
                                            Customization menu
                                        </button>
                                    </div>

                                    {menuMode === MENU_MODE_STANDARD ? (
                                        <div className="catering-quote-create-grid">
                                            <label className="catering-quote-create-field catering-quote-create-field--wide">
                                                <span>Package *</span>
                                                <select
                                                    value={cateringTempId}
                                                    onChange={(e) => handlePackageChange(e.target.value)}
                                                    disabled={loadingMenu || packages.length === 0}
                                                >
                                                    <option value="">Select package</option>
                                                    {packages.map((pkg) => (
                                                        <option
                                                            key={pkg.cateringTempId}
                                                            value={pkg.cateringTempId}
                                                        >
                                                            {pkg.packageName ||
                                                                `Package #${pkg.cateringTempId}`}
                                                        </option>
                                                    ))}
                                                </select>
                                            </label>
                                        </div>
                                    ) : null}

                                    {loadingMenu ? (
                                        <p className="catering-quote-create-muted">Loading catering menu…</p>
                                    ) : menuMode === MENU_MODE_STANDARD && packages.length === 0 ? (
                                        <p className="restaurant-detail-message error">
                                            No catering packages found.
                                        </p>
                                    ) : menuMode === MENU_MODE_CUSTOMIZATION && menuItems.length === 0 ? (
                                        <p className="restaurant-detail-message error">
                                            No catering menu items found.
                                        </p>
                                    ) : menuMode === MENU_MODE_STANDARD && loadingPackage ? (
                                        <p className="catering-quote-create-muted">Loading package items…</p>
                                    ) : menuMode === MENU_MODE_STANDARD &&
                                      cateringTempId &&
                                      !loadingPackage &&
                                      packageItems.length === 0 ? (
                                        <p className="restaurant-detail-message error">
                                            This package has no menu items.
                                        </p>
                                    ) : menuMode === MENU_MODE_STANDARD && !cateringTempId ? (
                                        <p className="catering-quote-create-muted">
                                            Select a package to view its menu items.
                                        </p>
                                    ) : showMenuItemsTable ? (
                                        <>
                                            <div className="catering-quote-create-section-head">
                                                <p className="catering-quote-create-muted catering-quote-create-menu-hint">
                                                    {menuMode === MENU_MODE_STANDARD
                                                        ? 'Adjust quantities or remove items from the package.'
                                                        : 'Add items from the catering menu.'}
                                                </p>
                                                {menuMode === MENU_MODE_CUSTOMIZATION ? (
                                                    <button
                                                        type="button"
                                                        className="menu-toolbar-btn"
                                                        onClick={() =>
                                                            setLineRows((rows) => [...rows, emptyLineRow()])
                                                        }
                                                    >
                                                        <FaPlus aria-hidden /> Add item
                                                    </button>
                                                ) : null}
                                            </div>
                                            <div className="catering-quote-create-lines-wrap">
                                                <table className="order-details-rich-table catering-quote-create-lines">
                                                    <thead>
                                                        <tr>
                                                            <th>Item</th>
                                                            <th>Qty</th>
                                                            <th>Unit ₹</th>
                                                            <th>Line ₹</th>
                                                            <th>Remove</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {lineRows.map((row) => {
                                                            const item = itemLookupById.get(
                                                                String(row.cateringItemId)
                                                            );
                                                            const qty = Number.parseInt(row.quantity, 10);
                                                            const unit = item
                                                                ? Number.parseFloat(item.Price)
                                                                : NaN;
                                                            const lineTotal =
                                                                Number.isFinite(unit) &&
                                                                Number.isFinite(qty) &&
                                                                qty > 0
                                                                    ? unit * qty
                                                                    : 0;
                                                            return (
                                                                <tr key={row.localKey}>
                                                                    <td>
                                                                        {menuMode === MENU_MODE_STANDARD ? (
                                                                            item?.Food_Item || '—'
                                                                        ) : (
                                                                            <select
                                                                                value={row.cateringItemId}
                                                                                onChange={(e) =>
                                                                                    updateLineRow(row.localKey, {
                                                                                        cateringItemId:
                                                                                            e.target.value,
                                                                                    })
                                                                                }
                                                                            >
                                                                                <option value="">
                                                                                    Select item
                                                                                </option>
                                                                                {selectableMenuItems.map(
                                                                                    (menuItem) => (
                                                                                        <option
                                                                                            key={
                                                                                                menuItem.Catering_Item_Id
                                                                                            }
                                                                                            value={
                                                                                                menuItem.Catering_Item_Id
                                                                                            }
                                                                                        >
                                                                                            {menuItem.Food_Item}
                                                                                        </option>
                                                                                    )
                                                                                )}
                                                                            </select>
                                                                        )}
                                                                    </td>
                                                                    <td>
                                                                        <input
                                                                            type="number"
                                                                            min="1"
                                                                            value={row.quantity}
                                                                            onChange={(e) =>
                                                                                updateLineRow(row.localKey, {
                                                                                    quantity: e.target.value,
                                                                                })
                                                                            }
                                                                        />
                                                                    </td>
                                                                    <td>
                                                                        {item ? formatMoney(item.Price) : '—'}
                                                                    </td>
                                                                    <td>{formatMoney(lineTotal)}</td>
                                                                    <td>
                                                                        <button
                                                                            type="button"
                                                                            className="action-button delete-button"
                                                                            onClick={() =>
                                                                                removeLineRow(row.localKey)
                                                                            }
                                                                            title="Remove item"
                                                                            aria-label="Remove item"
                                                                        >
                                                                            <FaTrash aria-hidden />
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </>
                                    ) : null}
                                </section>

                                <section className="catering-quote-create-section">
                                    <h4 className="catering-quote-create-section-title">Estimated quote *</h4>
                                    <div className="catering-quote-create-grid">
                                        <label className="catering-quote-create-field">
                                            <span>Amount (₹)</span>
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={estimatedQuote}
                                                onChange={(e) => setEstimatedQuote(e.target.value)}
                                                placeholder={
                                                    menuMode === MENU_MODE_STANDARD
                                                        ? 'Select package and enter guests'
                                                        : 'Enter quoted amount'
                                                }
                                            />
                                        </label>
                                    </div>
                                </section>

                                <div className="catering-quote-create-actions">
                                    <button
                                        type="button"
                                        className="menu-toolbar-btn"
                                        onClick={() => navigate(CATERING_QUOTES_LIST_PATH)}
                                        disabled={saving}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="menu-toolbar-btn menu-toolbar-btn--primary"
                                        disabled={saving || !canSaveQuote}
                                    >
                                        {saving ? 'Saving…' : 'Save quote'}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </RestaurantDashboardLayout>
    );
};

export default CreateCateringQuote;
